import type { EstadoSimulacao } from '@mymaxx/shared';
import { Aviso, Botao } from '@mymaxx/ui';
import { Play, RotateCcw, Send, SkipForward, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ErroApi } from '../lib/api';
import { fluxosApi } from '../lib/fluxos-api';

/**
 * Chat de teste do fluxo. Roda no servidor com o mesmo executor do atendimento real:
 * consulta o HubSoft de verdade e respeita o horário dos setores.
 */
export function Simulador({
  fluxoId,
  temRascunhoNaoPublicado,
  temVersaoPublicada,
  onNoAtual,
  onFechar,
}: {
  fluxoId: string;
  temRascunhoNaoPublicado: boolean;
  temVersaoPublicada: boolean;
  /** Avisa o editor qual nó destacar no canvas. */
  onNoAtual: (noId: string | null) => void;
  onFechar: () => void;
}) {
  const [estado, setEstado] = useState<EstadoSimulacao | null>(null);
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [usarRascunho, setUsarRascunho] = useState(true);
  const fimDaLista = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ behavior: 'smooth' });
  }, [estado?.mensagens.length]);

  useEffect(() => {
    onNoAtual(estado?.noAtual ?? null);
  }, [estado?.noAtual, onNoAtual]);

  // Ao sair, libera a sessão no servidor.
  useEffect(() => {
    return () => {
      const id = estado?.sessaoId;
      if (id) void fluxosApi.encerrarSimulacao(id).catch(() => undefined);
    };
  }, [estado?.sessaoId]);

  async function acao(fn: () => Promise<EstadoSimulacao>) {
    setOcupado(true);
    setErro(null);
    try {
      setEstado(await fn());
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao executar o teste');
    } finally {
      setOcupado(false);
    }
  }

  const iniciar = () => acao(() => fluxosApi.iniciarSimulacao(fluxoId, usarRascunho));

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (!estado || !texto.trim()) return;
    const mensagem = texto;
    setTexto('');
    void acao(() => fluxosApi.responderSimulacao(estado.sessaoId, mensagem));
  };

  return (
    <aside className="flex w-96 shrink-0 flex-col border-l border-neutral-200 dark:border-neutral-800">
      <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <div>
          <p className="text-sm font-medium">Testar fluxo</p>
          <p className="text-xs text-neutral-500">Execução real, sem criar atendimento.</p>
        </div>
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar teste"
          className="rounded p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
        >
          <X size={16} />
        </button>
      </header>

      {!estado && (
        <div className="space-y-3 p-4">
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">O que testar</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={usarRascunho} onChange={() => setUsarRascunho(true)} />
              Rascunho (com suas alterações{temRascunhoNaoPublicado ? ' não publicadas' : ''})
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={!usarRascunho}
                disabled={!temVersaoPublicada}
                onChange={() => setUsarRascunho(false)}
              />
              Versão publicada {temVersaoPublicada ? '(a que está no ar)' : '(ainda não existe)'}
            </label>
          </fieldset>
          {erro && <Aviso tipo="erro">{erro}</Aviso>}
          <Botao className="w-full" onClick={iniciar} carregando={ocupado}>
            <Play size={16} /> Iniciar teste
          </Botao>
          <p className="text-xs text-neutral-500">
            Dica: use o CPF <code>529.982.247-25</code> para testar com o cliente de exemplo do ERP.
          </p>
        </div>
      )}

      {estado && (
        <>
          <div className="flex-1 space-y-2 overflow-y-auto bg-neutral-50 p-3 dark:bg-neutral-950">
            {estado.mensagens.map((m, i) => (
              <div
                key={i}
                className={
                  m.de === 'cliente'
                    ? 'ml-auto max-w-[80%] rounded-lg rounded-br-none bg-marca-600 px-3 py-2 text-sm text-white'
                    : m.de === 'bot'
                      ? 'mr-auto max-w-[80%] rounded-lg rounded-bl-none bg-white px-3 py-2 text-sm whitespace-pre-line shadow-sm dark:bg-neutral-800'
                      : 'mx-auto max-w-[90%] rounded bg-amber-100 px-2 py-1 text-center text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                }
              >
                {m.texto}
              </div>
            ))}
            <div ref={fimDaLista} />
          </div>

          {Object.keys(estado.variaveis).length > 0 && (
            <details className="border-t border-neutral-200 px-3 py-2 text-xs dark:border-neutral-800">
              <summary className="cursor-pointer text-neutral-500">
                Variáveis coletadas ({Object.keys(estado.variaveis).length})
              </summary>
              <pre className="mt-2 max-h-32 overflow-auto rounded bg-neutral-100 p-2 text-[11px] dark:bg-neutral-900">
                {JSON.stringify(estado.variaveis, null, 2)}
              </pre>
            </details>
          )}

          {erro && (
            <div className="px-3 pt-2">
              <Aviso tipo="erro">{erro}</Aviso>
            </div>
          )}

          <div className="border-t border-neutral-200 p-3 dark:border-neutral-800">
            {estado.finalizado ? (
              <Botao
                variante="secundario"
                className="w-full"
                onClick={iniciar}
                carregando={ocupado}
              >
                <RotateCcw size={16} /> Testar de novo
              </Botao>
            ) : estado.aguardando === 'timeout' ? (
              <Botao
                variante="secundario"
                className="w-full"
                carregando={ocupado}
                onClick={() => void acao(() => fluxosApi.pularEspera(estado.sessaoId))}
              >
                <SkipForward size={16} /> Pular a espera
              </Botao>
            ) : (
              <form className="flex gap-2" onSubmit={enviar}>
                <input
                  value={texto}
                  autoFocus
                  aria-label="Resposta do cliente"
                  placeholder="Responda como o cliente…"
                  onChange={(e) => setTexto(e.target.value)}
                  className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                />
                <Botao type="submit" carregando={ocupado} aria-label="Enviar">
                  <Send size={16} />
                </Botao>
              </form>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
